import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApp } from "../../../context/AppState";
import { resolvePreviewIsMobile } from "../../../helper";
import { getStepSectionsForPopup } from "../../../utils/popup-step-sections";
import {
  ackDeselectToParent,
  ackSelectBlockToParent,
  isDeselectBlockMessage,
  parseSelectBlockMessage,
} from "../../../utils/preview-selection-messages";
import { isSectionVisible } from "../../../../shared/section-visibility.js";
import ExtensionPreviewSlot from "../ExtensionPreviewSlot";
import ButtonBlock from "../../../components/Preview/Draggable/Block/ButtonBlock";
import PostPurchaseCtaButton from "../post-purchase/PostPurchaseCtaButton";
import { indexPostPurchaseUpsellSections } from "../mapPostPurchaseUpsellSchema";
import { ensureExtensionBrandAttributionSections } from "./ensure-extension-brand-attribution";
import {
  extensionSurfaceUsesThemeSettings,
  resolveExtensionSurface,
} from "../../../../shared/extension-preview.js";
import ctaStyles from "./extension-upsell-widget.module.css";

export function normalizeStoreLabel(shop) {
  const raw = shop?.shop || shop?.name || "Your store";
  return String(raw)
    .replace(".myshopify.com", "")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

export default function useExtensionSurfacePreviewState({
  popupData,
  popupState,
  setPopupState,
}) {
  const {
    appState,
    isMobile: viewportIsMobile,
    previewDeviceIsMobile,
    popupGlobalData,
  } = useApp();

  const isMobile = resolvePreviewIsMobile(
    appState,
    viewportIsMobile,
    previewDeviceIsMobile
  );

  const popupType = popupData?.popUpType;
  const popupId = popupData?._id;
  const popupRef = useRef(null);

  const activeStep = useMemo(() => {
    const fromPopupState = popupState?.activeStep;
    if (typeof fromPopupState === "number" && !Number.isNaN(fromPopupState)) {
      return fromPopupState;
    }
    if (
      appState?.is_preview_mode &&
      typeof appState?.activeStep === "number" &&
      !Number.isNaN(appState.activeStep)
    ) {
      return appState.activeStep;
    }
    return 0;
  }, [popupState?.activeStep, appState?.is_preview_mode, appState?.activeStep]);

  const stepSections = useMemo(
    () => getStepSectionsForPopup(popupData, activeStep),
    [popupData, activeStep]
  );

  const mapped = useMemo(
    () =>
      indexPostPurchaseUpsellSections(
        ensureExtensionBrandAttributionSections(stepSections)
      ),
    [stepSections]
  );

  const currentParentData = useMemo(
    () => popupGlobalData?.[`${popupId}_${popupType}`] ?? {},
    [popupGlobalData, popupId, popupType]
  );

  const [selectedBlock, setSelectedBlock] = useState({});
  const selectedBlockRef = useRef(selectedBlock);
  selectedBlockRef.current = selectedBlock;

  const storeLabel = useMemo(
    () => normalizeStoreLabel(appState?.shop),
    [appState?.shop]
  );

  const useThemeSettings = useMemo(
    () =>
      extensionSurfaceUsesThemeSettings(
        resolveExtensionSurface(popupData, appState?.extensionSurface)
      ),
    [popupData, appState?.extensionSurface]
  );

  const handleBlockClick = useCallback(
    (entry) => {
      if (!appState?.is_preview_mode || !entry) return;

      const { section, index, subIndex, scope } = entry;
      const type = section?.type;
      const prev = selectedBlockRef.current;

      if (scope === "product" && typeof subIndex === "number") {
        if (prev.type === type && prev.index === subIndex && prev.parentIndex === index) {
          return;
        }
        ackSelectBlockToParent({ type, index, subIndex });
        setSelectedBlock({ type, index: subIndex, parentIndex: index });
        return;
      }

      if (prev.type === type && prev.index === index) return;
      ackSelectBlockToParent({ type, index });
      setSelectedBlock({ type, index });
    },
    [appState?.is_preview_mode]
  );

  const handleOutSideBlockClick = useCallback(() => {
    setSelectedBlock({});
    if (appState?.is_preview_mode) {
      ackDeselectToParent();
    }
  }, [appState?.is_preview_mode]);

  useEffect(() => {
    if (!appState?.is_preview_mode) return undefined;

    const handleOutsideClick = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        handleOutSideBlockClick();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [appState?.is_preview_mode, handleOutSideBlockClick]);

  useEffect(() => {
    const onMessage = (event) => {
      const data = event?.data;
      if (!data || typeof data !== "object") return;

      if (isDeselectBlockMessage(data)) {
        setSelectedBlock({});
        return;
      }

      const selection = parseSelectBlockMessage(data);
      if (!selection) return;

      const { type, index, subIndex } = selection;
      if (typeof subIndex === "number" && subIndex >= 0) {
        setSelectedBlock({ type, index: subIndex, parentIndex: index });
        return;
      }
      setSelectedBlock({ type, index });
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const handleClose = useCallback(() => {
    setPopupState((prev) => ({
      ...prev,
      [`${popupType}_${popupId}`]: {
        ...prev?.[`${popupType}_${popupId}`],
        openPopup: false,
      },
    }));
  }, [popupId, popupType, setPopupState]);

  const isSelected = useCallback(
    (entry) => {
      if (!entry) return false;
      if (entry.scope === "product" && typeof entry.subIndex === "number") {
        return (
          selectedBlock.type === entry.section?.type &&
          selectedBlock.index === entry.subIndex &&
          selectedBlock.parentIndex === entry.index
        );
      }
      return (
        selectedBlock.type === entry.section?.type &&
        selectedBlock.index === entry.index
      );
    },
    [selectedBlock]
  );

  const renderCta = useCallback(
    (entry, isPrimary = false) => {
      if (!entry || !isSectionVisible(entry.section)) return null;

      const productData = entry.parentSection?.products?.[0];
      const ctaIndex = entry.scope === "product" ? entry.subIndex : entry.index;
      const ctaProps = {
        popupData,
        popupState,
        setPopupState,
        section: entry.section,
        handleClose,
        index: ctaIndex,
        productIndex: entry.scope === "product" ? 0 : undefined,
        productData: entry.scope === "product" ? productData : undefined,
        parentData: currentParentData,
      };

      return (
        <ExtensionPreviewSlot
          isSelected={isSelected(entry)}
          onClick={() => handleBlockClick(entry)}
        >
          {useThemeSettings ? (
            <div className={ctaStyles.themeCtaWrap}>
              <ButtonBlock {...ctaProps} />
            </div>
          ) : (
            <PostPurchaseCtaButton primary={isPrimary} {...ctaProps} />
          )}
        </ExtensionPreviewSlot>
      );
    },
    [
      currentParentData,
      handleBlockClick,
      handleClose,
      isSelected,
      popupData,
      popupState,
      setPopupState,
      useThemeSettings,
    ]
  );

  return {
    popupRef,
    isMobile,
    activeStep,
    mapped,
    currentParentData,
    storeLabel,
    selectedBlock,
    handleBlockClick,
    isSelected,
    renderCta,
  };
}
